import React, { useState, useContext } from 'react';
import { Card, Typography, Avatar, Divider, Space, Tag, Button, Modal, Form, Input, Upload, message, InputNumber } from 'antd';
import { UserOutlined, MailOutlined, GithubOutlined, LinkOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { AuthContext } from '../../contexts/AuthContext';
import { defaultAboutInfoTemplate } from '../../api/mockData';
import './Profile.css';

const { Title, Text, Paragraph } = Typography;

const Profile = () => {
  const { user, logout, updateUser } = useContext(AuthContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');

  if (!user) {
    return (
      <div className="profile-page">
        <Card className="profile-card" style={{ textAlign: 'center' }}>
          <Title level={3}>请先登录</Title>
          <Paragraph>您需要登录后才能访问个人中心页面。</Paragraph>
        </Card>
      </div>
    );
  }

  const handleEditProfile = () => {
    form.setFieldsValue({
      name: user.name,
      intro: user.intro || '暂无个人介绍',
      details: user.details || '暂无详细介绍',
      email: user.email,
      techStack: user.techStack || '',
      interests: user.interests || '',
      github: user.github || '',
      linkedin: user.linkedin || '',
      personalHomepage: user.personalHomepage || '',
    });
    setIsModalVisible(true);
  };

  const handleSaveProfile = (values) => {
    // 更新用户信息
    const updatedUserInfo = {
      ...values,
      avatar: avatarUrl || user.avatar // 使用新上传的头像或保持原头像
    };

    updateUser(updatedUserInfo);
    message.success('个人信息更新成功');
    setIsModalVisible(false);
  };

  const handleLogout = () => {
    logout();
    message.info('已退出登录');
  };

  const uploadProps = {
    name: 'avatar',
    maxCount: 1,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 格式的图片!');
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过 2MB!');
      }
      return isJpgOrPng && isLt2M;
    },
    onChange(info) {
      if (info.file.status === 'done') {
        // 获取文件的URL
        const reader = new FileReader();
        reader.onload = (e) => {
          const url = e.target.result;
          setAvatarUrl(url);
          // 更新用户头像
          updateUser({ avatar: url });
        };
        reader.readAsDataURL(info.file.originFileObj);
        
        message.success(`${info.file.name} 上传成功`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} 上传失败`);
      }
    },
    onRemove() {
      setAvatarUrl('');
      // 设置为默认头像
      updateUser({ avatar: `${process.env.PUBLIC_URL}/qmau.jpg` });
    },
    listType: 'picture',
    showUploadList: true,
  };

  return (
    <div className="profile-page">
      <Card className="profile-card">
        <div className="profile-header">
          <Avatar size={100} src={user.avatar} icon={<UserOutlined />} />
          <div className="profile-basic-info">
            <Title level={2}>{user.name}</Title>
            <Text type="secondary">{user.intro || '暂无个人介绍'}</Text>
          </div>
          <Space size="middle">
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              onClick={handleEditProfile}
            >
              编辑资料
            </Button>
            <Button 
              danger
              onClick={handleLogout}
            >
              退出登录
            </Button>
          </Space>
        </div>

        <Divider />

        <div className="profile-details">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={4}>个人简介</Title>
              <Paragraph>{user.details || '暂无详细介绍'}</Paragraph>
            </div>

            <div>
              <Title level={4}>技术栈</Title>
              <div className="tags-section">
                {user.techStack && user.techStack.split(',').map((tech, index) => (
                  <Tag key={index} color="blue">{tech.trim()}</Tag>
                ))}
                {(!user.techStack || user.techStack === '暂无') && (
                  <Text type="secondary">暂无</Text>
                )}
              </div>
            </div>

            <div>
              <Title level={4}>兴趣爱好</Title>
              <div className="tags-section">
                {user.interests && user.interests.split(',').map((interest, index) => (
                  <Tag key={index} color="green">{interest.trim()}</Tag>
                ))}
                {(!user.interests || user.interests === '暂无') && (
                  <Text type="secondary">暂无</Text>
                )}
              </div>
            </div>

            <div>
              <Title level={4}>联系方式</Title>
              <Space direction="vertical" size="small">
                <div>
                  <MailOutlined style={{ marginRight: 8 }} />
                  <Text strong>邮箱: </Text>
                  <Text>{user.email}</Text>
                </div>
              </Space>
            </div>

            <div>
              <Title level={4}>社交媒体</Title>
              <Space size="large">
                {user.github && user.github !== '#' && (
                  <a href={user.github} target="_blank" rel="noopener noreferrer">
                    <GithubOutlined style={{ fontSize: '24px' }} />
                  </a>
                )}
                {user.linkedin && user.linkedin !== '#' && (
                  <a href={user.linkedin} target="_blank" rel="noopener noreferrer">
                    <span style={{ fontSize: '24px' }}>in</span>
                  </a>
                )}
                {user.personalHomepage && user.personalHomepage !== '#' && (
                  <a href={user.personalHomepage} target="_blank" rel="noopener noreferrer">
                    <LinkOutlined style={{ fontSize: '24px' }} />
                  </a>
                )}
              </Space>
            </div>
          </Space>
        </div>
      </Card>

      {/* 编辑资料模态框 */}
      <Modal
        title="编辑个人资料"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProfile}
        >
          <Form.Item label="头像">
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>上传头像</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="name" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}>
            <Input placeholder="请输入昵称" />
          </Form.Item>

          <Form.Item name="intro" label="一句话介绍" rules={[{ required: true, message: '请输入一句话介绍' }]}>
            <Input placeholder="请输入一句话介绍" />
          </Form.Item>

          <Form.Item name="details" label="详细介绍">
            <Input.TextArea rows={4} placeholder="请输入详细介绍" />
          </Form.Item>

          <Form.Item name="email" label="邮箱" rules={[{ type: 'email', message: '请输入正确的邮箱格式' }]}>
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item name="techStack" label="技术栈">
            <Input placeholder="例如：React, Node.js, Python" />
          </Form.Item>

          <Form.Item name="interests" label="兴趣爱好">
            <Input placeholder="例如：编程, 旅行, 摄影" />
          </Form.Item>

          <Form.Item name="github" label="GitHub">
            <Input placeholder="请输入GitHub链接" />
          </Form.Item>

          <Form.Item name="linkedin" label="LinkedIn">
            <Input placeholder="请输入LinkedIn链接" />
          </Form.Item>

          <Form.Item name="personalHomepage" label="个人主页">
            <Input placeholder="请输入个人主页链接" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">保存</Button>
              <Button onClick={() => setIsModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;